// The "Wi-Fi printers" dialog: the print server printers this browser knows, adding one (a new key, or
// a login link) and showing a login link / QR code for another browser (server/API.md, "Pairing and recovery").
import qrcode from 'qrcode-generator';
import { buildLoginLink, deriveFromKey, deriveFromSecret, generateKey, parseLoginFragment } from './crypto.js';
import { addPrinter } from './keystore.js';
import { disableNotifications, enableNotifications, PUSH_MESSAGES, pushSupported } from './push.js';
import { ReaderClient } from './serverclient.js';
import { button, enterPresses, openWizardDialog, paragraph } from '../device/wizard_dialog.js';

export const printerLabel = (rec) => rec.name || `Printer ${rec.id.slice(0, 6)}`;

// Adds the printers of a login link (the text after "#login=" or the whole link / hash). Returns the records.
export async function addFromLoginText(keyStore, text) {
  const hash = text.includes('#') ? text.slice(text.indexOf('#')) : text.startsWith('login=') ? `#${text}` : `#login=${text}`;
  const added = [];
  for (const item of parseLoginFragment(hash)) {
    const keys = await deriveFromSecret(item.kind, item.secret);
    added.push(await addPrinter(keyStore, item, keys.id));
  }
  return added;
}

function qrSvg(text) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const holder = document.createElement('div');
  holder.className = 'qr';
  holder.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  return holder;
}

// getActive(): id of the printer shown now, or null for the USB device. onUse(rec): show this printer.
// onChanged(): the list of printers changed (add, rename, forget).
export function openPrintersDialog({ keyStore, getActive, onUse, onChanged, getDevice = () => null }) {
  const dlg = openWizardDialog({ onRequestClose: () => dlg.close() });
  const show = dlg.show;

  const input = (placeholder, id) => Object.assign(document.createElement('input'), { type: 'text', placeholder, id, autocomplete: 'off', spellcheck: false });

  async function list() {
    const recs = (await keyStore.loadAll()).sort((a, b) => a.added - b.added);
    const nodes = [];
    nodes.push(paragraph('Printers on a print server. Their prints are encrypted by the device and can only be read with the key, which stays in this browser (and in the device). Browsers do not sync it between computers: use a login link for that.'));
    if (recs.length === 0) nodes.push(paragraph('<em>No Wi-Fi printers yet.</em>'));
    for (const rec of recs) {
      const row = document.createElement('div');
      row.className = 'printer-row';
      const label = document.createElement('span');
      label.innerHTML = '';
      label.textContent = `${printerLabel(rec)} `;
      const small = document.createElement('small');
      small.textContent = `${rec.id} · ${rec.kind === 'full' ? 'full key' : 'reader link'}${getActive() === rec.id ? ' · shown' : ''}`;
      label.append(small);
      const actions = document.createElement('span');
      actions.className = 'printer-actions';
      actions.append(
        button('Show', () => { onUse(rec); dlg.close(); }, 'compact'),
        button('Login link', () => linkView(rec), 'compact outline secondary'),
        button('Rename', () => renameView(rec), 'compact outline secondary'),
        button('Forget', () => forgetView(rec), 'compact outline secondary'),
      );
      row.append(label, actions);
      const item = document.createElement('div');
      item.className = 'printer-item';
      item.append(row, followToggle(rec, recs));
      nodes.push(item);
    }

    const create = input('Name (optional)', 'printer-new-name');
    const make = button('Create a new printer key', async () => {
      const K = generateKey();
      const keys = await deriveFromKey(K);
      const rec = await addPrinter(keyStore, { kind: 'full', secret: K }, keys.id, create.value.trim());
      onChanged();
      linkView(rec, true);
    });
    const paste = input('Paste a login link', 'printer-paste');
    const add = button('Add', async () => {
      try {
        const added = await addFromLoginText(keyStore, paste.value.trim());
        if (added.length === 0) {
          message.textContent = 'That is not a login link.';
          return;
        }
        onChanged();
        list();
      } catch {
        message.textContent = 'That is not a login link.';
      }
    });
    const message = document.createElement('small');
    // Re-authentication by plugging the device in: the key is read out of it (physical access is the trust model).
    const fromDevice = button('Take the key from the connected device', async () => {
      const device = getDevice();
      if (!device) {
        message.textContent = 'Connect the device first.';
        return;
      }
      try {
        const key = await device.getKey();
        if (!key) {
          message.textContent = 'The device has no key yet: set it up with Set up, Wi-Fi printing.';
          return;
        }
        const keys = await deriveFromKey(key);
        await addPrinter(keyStore, { kind: 'full', secret: key }, keys.id);
        onChanged();
        list();
      } catch (e) {
        message.textContent = `Could not read the key: ${e.message}`;
      }
    }, 'outline secondary');
    const addBox = document.createElement('fieldset');
    addBox.className = 'printer-add';
    addBox.append(create, make, paste, add, fromDevice, message);
    enterPresses(create, make);
    enterPresses(paste, add);
    nodes.push(addBox);
    show('Wi-Fi printers', nodes, [button('Close', () => dlg.close(), 'outline secondary')]);
  }

  // "Notify me when a print starts": opt in per printer; the browser's own permission question comes from this click
  // and from nowhere else (push.js). One browser can follow several printers, several browsers one printer.
  function followToggle(rec, recs) {
    const wrap = document.createElement('div');
    wrap.className = 'printer-follow';
    const label = document.createElement('label');
    const box = Object.assign(document.createElement('input'), { type: 'checkbox', checked: Boolean(rec.notify) });
    box.setAttribute('role', 'switch');
    label.append(box, ' Notify me when a print starts, also when this page is closed');
    const note = document.createElement('small');
    wrap.append(label, note);
    if (!pushSupported()) {
      box.disabled = true;
      note.textContent = PUSH_MESSAGES.unsupported;
      return wrap;
    }
    // derived now, so that the click handler reaches the browser's permission question without waiting for anything
    const keys = deriveFromSecret(rec.kind, rec.secret);
    box.onchange = async () => {
      const on = box.checked;
      box.disabled = true;
      note.textContent = '';
      try {
        const client = new ReaderClient({ keys: await keys });
        if (on) await enableNotifications({ client });
        else await disableNotifications({ client, othersFollow: recs.some((r) => r.id !== rec.id && r.notify) });
        rec.notify = on;
        await keyStore.save({ ...rec, notify: on });
        note.textContent = on ? 'On: this browser will be told when a print starts on this printer.' : '';
      } catch (e) {
        box.checked = !on;
        note.textContent = e.reason ? e.message : `That did not work: ${e.message}`;
      }
      box.disabled = false;
    };
    return wrap;
  }

  // Rename and Forget are steps of this dialog, not window.prompt()/confirm().
  function renameView(rec) {
    const name = input('Name (only shown in this browser)', 'printer-rename');
    name.value = rec.name || '';
    const save = async () => {
      await keyStore.save({ ...rec, name: name.value.trim() });
      onChanged();
      list();
    };
    show(`Rename ${printerLabel(rec)}`, [paragraph('The name is only shown in this browser.'), name], [button('Cancel', list, 'outline secondary'), button('Save', save)]);
    name.focus();
  }

  function forgetView(rec) {
    show(
      `Forget ${printerLabel(rec)}?`,
      [paragraph('Forget this printer in this browser? Without its key or a login link the prints on the server can no longer be read.')],
      [
        button('Cancel', list, 'outline secondary'),
        button('Forget', async () => {
          if (rec.notify) {
            // this browser stops following it (the server drops the subscription; failing to reach it changes nothing here)
            const client = new ReaderClient({ keys: await deriveFromSecret(rec.kind, rec.secret) });
            const others = (await keyStore.loadAll()).some((r) => r.id !== rec.id && r.notify);
            await disableNotifications({ client, othersFollow: others }).catch(() => {});
          }
          await keyStore.remove(rec.id);
          onChanged();
          list();
        }, 'danger'),
      ],
    );
  }

  async function linkView(rec, fresh = false) {
    const nodes = [];
    if (fresh) nodes.push(paragraph('<strong>New printer key created.</strong> Set a device up with a printer from the <em>Set up</em> menu (Wi-Fi printing). Keep this link: it is the only way to get at the prints from another browser.'));
    nodes.push(paragraph('Anyone who has this link can read <strong>all</strong> prints of this printer and delete them. The part after <code>#</code> never reaches the server.'));
    const reader = document.createElement('input');
    reader.type = 'checkbox';
    reader.id = 'link-reader';
    reader.checked = rec.kind !== 'full';
    reader.disabled = rec.kind !== 'full';
    const readerLabel = document.createElement('label');
    readerLabel.append(reader, ' Reader link: can read and delete prints, but cannot act as the device or set up another one');
    if (rec.kind === 'full') nodes.push(readerLabel);
    const field = Object.assign(document.createElement('input'), { type: 'text', readOnly: true });
    field.onfocus = () => field.select();
    const qrBox = document.createElement('div');
    nodes.push(field, qrBox);
    const draw = async () => {
      const keys = await deriveFromSecret(rec.kind, rec.secret);
      const asReader = reader.checked;
      const secret = asReader ? keys.R : rec.secret;
      field.value = buildLoginLink(location.origin, [{ kind: asReader ? 'reader' : 'full', secret }]);
      qrBox.replaceChildren(qrSvg(field.value));
    };
    reader.onchange = draw;
    await draw();
    const copy = button('Copy link', async () => {
      try {
        await navigator.clipboard.writeText(field.value);
        copy.textContent = 'Copied';
      } catch {
        field.select();
      }
    });
    show(`${printerLabel(rec)}: login link`, nodes, [button('Back', list, 'outline secondary'), button('Show this printer', () => { onUse(rec); dlg.close(); }, 'secondary'), copy]);
  }

  list();
}
